import { authService } from "./auth";
import type { 
  ListThreadsResponse,
  CreateThreadRequest,
  CreateThreadResponse,
  ContinueThreadRequest,
  ThreadInfo
} from "@/types/threads";

const API_BASE_URL = `${import.meta.env.VITE_API_BASE_URL}thread/`;

class ThreadService {
  private async getAuthHeaders() {
    const idToken = await authService.getIdToken();
    if (!idToken) {
      throw new Error('Authentication token not available');
    }
    
    return {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    };
  }

  /**
   * Create a new thread for a specific persona
   */
  async createThread(request: CreateThreadRequest): Promise<CreateThreadResponse> {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(API_BASE_URL, {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return {
        ...data,
        persona: request.personaId,
      } as CreateThreadResponse;
    } catch (error) {
      console.error('Error creating thread:', error);
      throw error;
    }
  }

  /**
   * Continue an existing conversation in a thread
   */
  async continueThread(threadId: string, request: ContinueThreadRequest): Promise<{ assistant: string }> {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${API_BASE_URL}${threadId}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return data as { assistant: string };
    } catch (error) {
      console.error('Error continuing thread:', error);
      throw error;
    }
  }

  /**
   * Get the list of all threads for the user
   */
  async listThreads(): Promise<ListThreadsResponse> {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(API_BASE_URL, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return data as ListThreadsResponse;
    } catch (error) {
      console.error('Error listing threads:', error);
      throw error;
    }
  }

  /**
   * Get the detailed information of a specific thread
   */
  async getThreadInfo(threadId: string): Promise<ThreadInfo> {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${API_BASE_URL}${threadId}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return data as ThreadInfo;
    } catch (error) {
      console.error('Error getting thread info:', error);
      throw error;
    }
  }

  /**
   * Send a message to a persona (continue an existing thread)
   */
  async sendMessage( userPrompt: string, threadId: string): Promise<{ 
    assistant: string; 
    threadId: string; 
    isNewThread: boolean;
  }> {
    try {
      if (!threadId) {
        throw new Error('ThreadId is required to send a message. The thread must be initialized first.');
      }
      
      // Continue an existing thread
      const response = await this.continueThread(threadId, { userPrompt });
      return {
        assistant: response.assistant,
        threadId,
        isNewThread: false,
      };
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }
}

export const threadService = new ThreadService(); 